// Demo mode: serves plausible fake data instead of calling the Skilz API.
//
// Enabled at build time with REACT_APP_DEMO=true. It exists so a reviewable
// build of the admin can be published anywhere — a preview link, an artifact,
// a static host — without credentials and without touching production records.
// The production bundle is unaffected: every call site checks IS_DEMO first.

export const IS_DEMO = process.env.REACT_APP_DEMO === 'true';

const FIRST = ['Layla', 'Omar', 'Sara', 'Yousef', 'Mariam', 'Khalid', 'Noor', 'Hassan',
    'Amira', 'Tariq', 'Dana', 'Rashid', 'Hala', 'Faisal', 'Zeina', 'Bilal'];
const LAST = ['Al Mansouri', 'Haddad', 'Khoury', 'Al Farsi', 'Nasser', 'Darwish',
    'Saleh', 'Aziz', 'Rahman', 'Habib'];
const CITIES = [['Dubai', 'UAE'], ['Abu Dhabi', 'UAE'], ['Sharjah', 'UAE'],
    ['Doha', 'Qatar'], ['Riyadh', 'Saudi Arabia'], ['Beirut', 'Lebanon']];
const SERVICES = ['Home Cleaning', 'AC Repair', 'Plumbing', 'Personal Training',
    'Math Tutoring', 'Photography', 'Car Detailing', 'Pet Grooming',
    'Electrical Work', 'Moving Help', 'Hair Styling', 'Catering'];

// Deterministic PRNG so the demo looks identical on every load and every build.
// A reviewer comparing two preview links should see data differences only when
// the code changed, never because the fixtures reshuffled.
let seed = 1337;
const rnd = () => ((seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
const pick = arr => arr[Math.floor(rnd() * arr.length)];
const int = (min, max) => Math.floor(rnd() * (max - min + 1)) + min;
const oid = i => (i + 1).toString(16).padStart(24, 'a');
const daysAgo = d => new Date(Date.UTC(2026, 7, 21) - d * 864e5).toISOString();

const users = Array.from({ length: 48 }, (_, i) => {
    const [city, country] = pick(CITIES);
    const name = `${pick(FIRST)} ${pick(LAST)}`;
    return {
        _id: oid(i),
        name,
        phone: `+9715${int(10, 59)}${int(100000, 999999)}`,
        email: `${name.split(' ')[0].toLowerCase()}${i}@example.com`,
        city,
        country,
        role: i === 0 ? 'admin' : 'user',
        status: rnd() > 0.9 ? 'banned' : 'active',
        balance: int(0, 4200),
        createdAt: daysAgo(int(1, 400)),
        // Fixed, distributed activity dates keep dashboard 7/30/90-day demo
        // ranges meaningful without making fixture output depend on wall time.
        lastSeen: new Date(Date.UTC(2026, 7, 30) - (i % 90) * 864e5).toISOString(),
        // Every 3rd demo user has never signed in, so the Users table renders
        // both a real date and the "Never signed in" placeholder.
        lastLoginAt: i % 3 === 2 ? null : daysAgo(int(0, 60)),
        transactions: Array.from({ length: int(0, 4) }, (_, t) => ({
            _id: `${oid(i)}-tx-${t}`,
            amount: int(25, 900),
            type: pick(['topup', 'payout', 'refund', 'fee']),
            status: pick(['completed', 'pending', 'failed']),
            createdAt: daysAgo(int(0, 90)),
        })),
    };
});

// Trimmed user reference, matching how the API embeds a user inside other docs.
const ref = u => ({ _id: u._id, name: u.name, phone: u.phone });
const someone = () => users[int(0, users.length - 1)];

const defaultservices = SERVICES.map((name, i) => ({
    _id: oid(200 + i),
    name,
    defaultDesc: `Vetted ${name.toLowerCase()} providers, booked through Skilz.`,
    tags: [name.split(' ')[0].toLowerCase(), 'featured'],
    category: pick(['Home', 'Wellness', 'Education', 'Automotive']),
    featuredImage: '',
    // The list splits on these exact uppercase values into Published vs Draft.
    status: rnd() > 0.3 ? 'PUBLISHED' : 'DRAFT',
    totalSearches: int(12, 4800),
    createdAt: daysAgo(int(30, 500)),
}));

const userservices = Array.from({ length: 36 }, (_, i) => {
    const u = someone();
    const s = defaultservices[int(0, defaultservices.length - 1)];
    const street = `${int(1, 90)} ${pick(['Marina', 'Jumeirah', 'Business Bay', 'Deira'])} St`;
    return {
        _id: oid(400 + i),
        createdBy: ref(u),
        defaultService: { _id: s._id, name: s.name },
        // The admin parses this comma-separated string rather than reading the
        // city/country fields, so the shape has to match the real API exactly.
        locationString: `${street}, ${u.city}, ${u.country}`,
        location: { coordinates: [55.27 + rnd() * 0.2, 25.2 + rnd() * 0.2] },
        price: int(50, 800),
        rating: (3 + rnd() * 2).toFixed(1),
        jobsCompleted: int(0, 180),
        status: rnd() > 0.88 ? 'banned' : 'active',
        createdAt: daysAgo(int(1, 300)),
    };
});

const requests = Array.from({ length: 30 }, (_, i) => {
    const from = someone();
    const target = userservices[int(0, userservices.length - 1)];
    return {
        _id: oid(600 + i),
        status: pick(['pending', 'accepted', 'completed', 'cancelled', 'negotiating']),
        createdBy: ref(from),
        createdFor: {
            _id: target._id,
            createdBy: target.createdBy,
            defaultService: target.defaultService,
        },
        defaultService: target.defaultService,
        price: int(60, 900),
        chatroom: oid(900 + i),
        createdAt: daysAgo(int(0, 60)),
    };
});

const message = (who, body, d) => ({
    _id: `m-${who}-${d}`,
    senderUser: { name: who },
    content: body,
    date: daysAgo(d),
});

const tickets = Array.from({ length: 18 }, (_, i) => {
    const u = someone();
    const status = pick(['open', 'open', 'closed-resolved', 'closed-unresolved']);
    return {
        _id: oid(800 + i),
        subject: pick(['Payment not received', 'Cannot verify phone', 'Provider no-show',
            'Refund request', 'Account locked', 'Wrong charge']),
        content: 'Details of the reported issue go here.',
        status,
        createdBy: ref(u),
        resolvedBy: status === 'closed-resolved' ? { name: 'Demo Admin' } : undefined,
        messages: [
            message(u.name, 'Hi, I need help with this.', int(2, 20)),
            message('Demo Admin', 'Thanks for reaching out — looking into it now.', int(0, 1)),
        ],
        createdAt: daysAgo(int(2, 45)),
        updatedAt: daysAgo(int(0, 2)),
    };
});

const feeds = Array.from({ length: 20 }, (_, i) => {
    const u = someone();
    const target = userservices[int(0, userservices.length - 1)];
    return {
        _id: oid(1000 + i),
        desc: pick(['Finished another job today!', 'Before and after', 'Booked solid this week',
            'New tools just arrived', 'Happy client, happy me']),
        attachement: '',
        createdBy: { _id: u._id, name: u.name },
        linkedUserService: { _id: target._id, defaultService: target.defaultService },
        likes: Array.from({ length: int(0, 5) }, () => {
            const liker = someone();
            return { id: liker._id, name: liker.name };
        }),
        comments: Array.from({ length: int(0, 6) }, (_, c) => {
            const author = someone();
            return { _id: `c-${i}-${c}`, body: 'Nice work!', createdBy: { _id: author._id, name: author.name } };
        }),
        nbOfReports: rnd() > 0.85 ? int(1, 7) : 0,
        createdAt: daysAgo(int(0, 120)),
    };
});

const REPORT_TARGETS = [
    { relatedType: 'requests', relatedId: oid(600 + 0) },
    { relatedType: 'requests', relatedId: oid(600 + 1) },
    { relatedType: 'requests', relatedId: oid(600 + 2) },
    { relatedType: 'users', relatedId: oid(0) },
    { relatedType: 'users', relatedId: oid(1) },
    { relatedType: 'feeds', relatedId: oid(1000 + 0) },
    { relatedType: 'feeds', relatedId: oid(1000 + 1) },
    { relatedType: 'feeds', relatedId: oid(1000 + 2) },
];

const logs = Array.from({ length: 90 }, (_, i) => {
    // A slice of logs have no user, mirroring guest traffic. The Logs page has a
    // dedicated "Guest User" bucket for these, so the fixtures must exercise it.
    const guest = rnd() > 0.85;
    const u = someone();
    const action = pick(['LOGIN', 'REPORT', 'BOOKING', 'PAYMENT', 'BAN', 'SIGNUP', 'SEARCH']);
    const base = {
        _id: oid(1200 + i),
        action,
        message: pick(['User signed in', 'Content reported', 'Booking confirmed',
            'Payment captured', 'Provider banned', 'New account created', 'Search performed']),
        user: guest ? null : { _id: u._id, name: u.name, phone: u.phone },
        createdAt: daysAgo(int(0, 30)),
    };
    if (action === 'REPORT') {
        const target = pick(REPORT_TARGETS);
        base.relatedType = target.relatedType;
        base.relatedId = target.relatedId;
        base.status = pick(['OPEN', 'OPEN', 'DONE']); // ~2/3 open
    }
    return base;
});

const chatroom = {
    _id: oid(9999),
    messages: [
        message('Layla Al Mansouri', 'Hi, are you available Saturday morning?', 3),
        message('Omar Nasser', 'Yes, I can be there at 9am.', 3),
        message('Layla Al Mansouri', 'Perfect, booking it now.', 2),
    ],
};

const COLLECTIONS = { users, defaultservices, userservices, requests, tickets, feeds, logs };

const page = docs => ({
    docs,
    totalDocs: docs.length,
    limit: docs.length,
    page: 1,
    totalPages: 1,
    hasNextPage: false,
    nextPage: null,
});

// Maps a request URL onto the fixtures. Mirrors what the real API returns for
// each endpoint the admin actually calls, including the paginated {docs} envelope.
// Aggregate-only fixture shaped exactly like GET /api/admin/journey. Scales by
// range so the 7/30/90 toggle visibly changes the chart in demo builds.
const demoJourney = rawDays => {
    const days = [7, 30, 90].includes(Number(rawDays)) ? Number(rawDays) : 30;
    const f = days / 30;
    const n = v => Math.round(v * f);
    const entries = [['/(tabs)/home', 420], ['/(tabs)/search', 96], ['/(tabs)/inbox', 74], ['/(tabs)/profile', 41], ['/request/:id', 22]]
        .map(([page, s]) => ({ page, sessions: n(s) }));
    const transitions = [
        ['/(tabs)/home', '/(tabs)/search', 160], ['/(tabs)/home', '/service/:id', 88], ['/(tabs)/home', '/(tabs)/inbox', 52],
        ['/(tabs)/search', '/service/:id', 48], ['/(tabs)/inbox', '/chat/:id', 51], ['/(tabs)/profile', '/verification', 12],
        ['/(tabs)/profile', '/(tabs)/profile/settings/my-plan', 9], ['/request/:id', '/chat/:id', 15],
    ].map(([from, to, s]) => ({ from, to, sessions: n(s) }));
    const total = entries.reduce((s, e) => s + e.sessions, 0);
    return {
        days,
        generatedAt: '2026-08-21T00:00:00.000Z',
        source: { tracked: false, reason: 'No traffic source is captured.' },
        sessions: { tracked: true, unit: 'logged-in app sessions', total, withNextPage: transitions.reduce((s, t) => s + t.sessions, 0), entries, transitions },
        funnel: {
            unit: 'users',
            cohort: 'Accounts created in the window (admins excluded); steps counted sequentially.',
            steps: [
                { id: 'register', label: 'Registered (phone OTP)', count: n(310) },
                { id: 'signup', label: 'Profile set up (name)', count: n(241) },
                { id: 'kyc_l1', label: 'KYC L1 verified', count: n(88) },
                { id: 'paid', label: 'Paid', count: n(23) },
            ],
            independent: { kycL1: n(92), kycL2: n(9), paid: n(31), paidSubscription: n(7), walletTopUp: n(19), walletBooking: n(14) },
            paidDefinition: 'Paid = non-FREE subscriptionTier, OR a confirmed wallet top-up, OR a confirmed in-app wallet booking payment.',
        },
    };
};

// Referral rewards (admin/referrals/*). Purely index-derived — deliberately
// does NOT draw from the shared PRNG, so adding these never reshuffles the
// other fixtures. Seeds both states of every optional field: all five
// statuses, null invitee and null displayName, null releasedAt, statusReason
// codes, null capMonth, and 23 rows so pagination (limit 20) has two pages.
export const DEMO_REFERRAL_INVITER_ID = oid(0);
const REFERRAL_STATUS_CYCLE = ['RELEASED', 'PENDING', 'RELEASED', 'CAPPED', 'RELEASED', 'VOIDED', 'RELEASED', 'REVERSED'];
// Every status and statusReason below is one the backend can actually store
// (skilzBackend, unmerged referral PR):
//   statuses       app/models/referralReward.js REWARD_STATUSES
//   CAPPED         `CAP_EXHAUSTED:${cappedBy}` app/services/referralRewardService.js,
//                  cappedBy ∈ CAPPED_BY app/services/referralRewardCalculator.js
//   VOIDED         `RELEASE_BLOCKED:${reason}` app/services/referralRewardRelease.js,
//                  reason from assessEligibility's VOID decisions only:
//                  DEAL_CANCELLED:<CANCELLED|AUTO_CANCELLED> (request.js status enum),
//                  DEAL_CANCELLED:cancelledAt, REFERRAL_REVOKED,
//                  DEAL_REFUNDED:transaction_<non-live tx status> (user.js
//                  transactions.status enum; live = confirmed|pending)
//   REVERSED       nothing in the backend writes a reason (or this status) yet → null
//   PENDING/RELEASED  statusReason null (release sets it to null)
// INVITER_<status> is a SKIP reason (row stays PENDING, nothing written), so it never appears.
export const DEMO_REFERRAL_REASONS = Object.freeze({
    CAPPED: Object.freeze(['CAP_EXHAUSTED:MONTHLY', 'CAP_EXHAUSTED:PER_DEAL', 'CAP_EXHAUSTED:LIFETIME']),
    VOIDED: Object.freeze([
        'RELEASE_BLOCKED:DEAL_CANCELLED:CANCELLED',
        'RELEASE_BLOCKED:REFERRAL_REVOKED',
        'RELEASE_BLOCKED:DEAL_REFUNDED:transaction_rejected',
    ]),
});
const REFERRAL_REASONS = DEMO_REFERRAL_REASONS;
const hexId = (prefix, i) => `${prefix}${(i + 1).toString(16).padStart(24 - prefix.length, '0')}`;
export const demoReferralRewards = inviterId => Array.from({ length: 23 }, (_, i) => {
    const status = REFERRAL_STATUS_CYCLE[i % REFERRAL_STATUS_CYCLE.length];
    const created = daysAgo(i * 2 + 1);
    const releaseAt = new Date(Date.parse(created) + 7 * 864e5).toISOString();
    const reasons = REFERRAL_REASONS[status];
    const n = i + 1;
    return {
        id: hexId('d0', i),
        reference: `RR-${String(1000 + 23 - i)}`,
        referralId: i % 7 === 6 ? null : hexId('d1', i % 9),
        inviterId,
        status,
        statusReason: reasons ? reasons[i % reasons.length] : null,
        amountMinor: status === 'CAPPED' ? 0 : 100 + (n % 4) * 125,
        currency: 'USD',
        capMonth: i % 11 === 10 ? null : created.slice(0, 7),
        createdAt: created,
        releaseAt,
        releasedAt: status === 'RELEASED' || status === 'REVERSED' ? new Date(Date.parse(releaseAt) + 3e5).toISOString() : null,
        requestId: i % 5 === 4 ? null : hexId('d2', i),
        transactionId: status === 'RELEASED' || status === 'REVERSED' ? hexId('d3', i) : null,
        dealCompletedAt: i % 6 === 5 ? null : new Date(Date.parse(created) - 36e5).toISOString(),
        invitee: i % 9 === 4 ? null
            : { id: users[(i + 1) % users.length]._id, displayName: i % 9 === 7 ? null : users[(i + 1) % users.length].name },
    };
});
const demoReferralSummary = inviterId => {
    const rows = demoReferralRewards(inviterId);
    const byStatus = {};
    ['PENDING', 'RELEASED', 'CAPPED', 'REVERSED', 'VOIDED'].forEach(s => {
        const of = rows.filter(r => r.status === s);
        byStatus[s] = { count: of.length, amountMinor: of.reduce((a, r) => a + r.amountMinor, 0) };
    });
    return {
        inviterId,
        currency: 'USD',
        referrals: { total: 12, attributed: 9, byStatus: { PENDING: 1, ATTRIBUTED: 9, EXPIRED: 1, SUPERSEDED: 0, REVOKED: 1 } },
        rewards: {
            total: rows.length,
            byStatus,
            totalEarnedMinor: byStatus.PENDING.amountMinor + byStatus.RELEASED.amountMinor,
            totalReleasedMinor: byStatus.RELEASED.amountMinor,
            totalPendingMinor: byStatus.PENDING.amountMinor,
        },
    };
};
const demoReferrals = (rest, query) => {
    // rest = ['referrals', 'users', id, 'summary'|'rewards'] | ['referrals', 'rewards', id]
    if (rest[1] === 'users' && rest[3] === 'summary') return demoReferralSummary(rest[2]);
    if (rest[1] === 'users' && rest[3] === 'rewards') {
        const q = new URLSearchParams(query || '');
        const status = q.get('status');
        const limit = Math.min(100, Math.max(1, parseInt(q.get('limit'), 10) || 20));
        const pageNo = Math.max(1, parseInt(q.get('page'), 10) || 1);
        const all = demoReferralRewards(rest[2]).filter(r => !status || r.status === status);
        const totalPages = Math.max(1, Math.ceil(all.length / limit));
        return {
            items: all.slice((pageNo - 1) * limit, pageNo * limit),
            page: pageNo, limit, total: all.length, totalPages, hasNextPage: pageNo < totalPages,
        };
    }
    if (rest[1] === 'rewards' && rest[2]) {
        const reward = demoReferralRewards(DEMO_REFERRAL_INVITER_ID).find(r => r.id === rest[2]);
        return { reward: reward || null };
    }
    return page([]);
};

export const demoResponse = (url, options = {}) => {
    const path = String(url).split('/api/')[1] || '';
    const [route, query] = path.split('?');
    const parts = route.split('/').filter(Boolean);
    const [head, ...rest] = parts;
    const method = (options.method || 'GET').toUpperCase();

    if (head === 'chatrooms') return chatroom;
    if (head === 'admin' && rest[0] === 'referrals') return demoReferrals(rest, query);
    if (head === 'admin' && rest[0] === 'journey') return demoJourney(new URLSearchParams(query || '').get('days'));
    if (head === 'authenticateFirebase') {
        return { token: 'demo-token', user: { _id: oid(0), name: 'Demo Admin', phone: '+971500000000', role: 'admin', status: 'active' } };
    }

    const collection = COLLECTIONS[head];
    if (!collection) return page([]);

    // Writes are acknowledged but not persisted — the demo build is a read-only
    // mirror, so a reviewer clicking Ban or Save sees success without any
    // fixture drifting out from under the rest of the screen.
    if (method !== 'GET') {
        const id = rest.find(p => /^[0-9a-f]{24}$/.test(p));
        const found = collection.find(d => d._id === id);
        return found ? { ...found, ...(safeParse(options.body) || {}) } : { ok: true };
    }

    if (rest.length && /^[0-9a-f]{24}$/.test(rest[rest.length - 1])) {
        const id = rest[rest.length - 1];
        return collection.find(d => d._id === id) || {};
    }

    if (query && query.includes('action=')) {
        const action = new URLSearchParams(query).get('action');
        return page(collection.filter(d => d.action === action));
    }

    return page(collection);
};

const safeParse = body => {
    try {
        return typeof body === 'string' ? JSON.parse(body) : null;
    } catch (e) {
        return null;
    }
};

export default demoResponse;
